/**
 * Where a clinic is filmed: the outside (building and approach) and the named
 * inside locations, each with photos and a note for the crew. Kept by staff on
 * the client page; the pre-production board picks from these. A retired
 * location folds away but stays on the boards that already picked it.
 */
import { useEffect, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { MapPin, Plus, ImagePlus, Trash2, Pencil, Archive, ArchiveRestore, X, StickyNote } from 'lucide-react';
import { toast } from 'sonner';
import { Card } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import PromptDialog from '@/components/ui/PromptDialog';
import ConfirmDialog from '@/components/ui/ConfirmDialog';
import {
  qk, fetchClientLocations, createClientLocation, patchClientLocation, deleteClientLocation, deleteLocationPhoto,
  type ClientLocation, type LocationPhoto,
} from '@/lib/queries';
import { uploadLocationPhoto } from '@/lib/upload';
import { cn } from '@/lib/cn';
import { useCan } from '@/hooks/use-scopes';

const errMsg = (e: unknown) => (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Something went wrong';

type Uploading = { locationId: string; done: number; total: number } | null;

export default function ClientLocationsCard({ clientId }: { clientId: string }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: qk.clientLocations(clientId), queryFn: () => fetchClientLocations(clientId) });
  const refresh = () => qc.invalidateQueries({ queryKey: qk.clientLocations(clientId) });
  const [name, setName] = useState('');
  const [note, setNote] = useState('');
  const [showRetired, setShowRetired] = useState(false);
  const [viewing, setViewing] = useState<LocationPhoto | null>(null);
  const [uploading, setUploading] = useState<Uploading>(null);
  const outsideInput = useRef<HTMLInputElement>(null);
  const canEdit = useCan('locations.write');

  const create = useMutation({
    mutationFn: () => createClientLocation(clientId, { kind: 'inside', name: name.trim(), note: note.trim() || undefined }),
    onSuccess: (l) => { toast.success(`${l.name} added`); setName(''); setNote(''); refresh(); },
    onError: (e) => toast.error(errMsg(e)),
  });

  const all = q.data ?? [];
  const outside = all.find((l) => l.kind === 'outside') ?? null;
  const inside = all.filter((l) => l.kind === 'inside');
  const live = inside.filter((l) => l.active);
  const retired = inside.filter((l) => !l.active);
  const photoCount = all.reduce((n, l) => n + l.photos.length, 0);

  /** Upload the picked images one after another; the outside location is made on its first photo. */
  async function addPhotos(locationId: string | null, files: File[]) {
    if (!files.length) return;
    // the caller copied the FileList before clearing the input, so this is a real array
    const list = files.filter((f) => f.type.startsWith('image/'));
    if (!list.length) { toast.error('Pick image files (JPG, PNG, HEIC)'); return; }
    let lid = locationId;
    try {
      if (!lid) { const o = await createClientLocation(clientId, { kind: 'outside' }); lid = o.id; }
      setUploading({ locationId: lid, done: 0, total: list.length });
      let ok = 0;
      for (const f of list) {
        try { await uploadLocationPhoto(clientId, lid, f); ok += 1; }
        catch (e) { toast.error(`${f.name}: ${errMsg(e)}`); }
        setUploading({ locationId: lid, done: ok, total: list.length });
      }
      if (ok) toast.success(ok === 1 ? 'Photo added' : `${ok} photos added`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setUploading(null); refresh();
    }
  }

  return (
    <Card className="mb-6" data-testid="locations-card">
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <MapPin size={16} className="text-accent-400" />
          <h2 className="font-medium text-muted-strong">Locations</h2>
          <span className="text-xs text-muted">{q.data ? `${live.length} inside · ${photoCount} photo${photoCount === 1 ? '' : 's'}` : ''}</span>
        </div>
        <span className="text-[11px] text-muted">Picked on the pre-production board; the crew sees the photos and the note.</span>
      </div>

      {q.isLoading && <div className="text-xs text-muted">Loading...</div>}

      {/* ---- outside */}
      <div className="rounded-lg bg-bg px-3 py-2.5 mb-3" data-testid="location" data-kind="outside" data-active="true">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm text-muted-strong font-medium">Outside</span>
          <span className="text-[11px] text-muted">the building and the approach</span>
          <div className="ml-auto flex items-center gap-2">
            {uploading && outside && uploading.locationId === outside.id && <span className="text-[11px] text-accent-400">Uploading {uploading.done} of {uploading.total}...</span>}
            <input ref={outsideInput} type="file" accept="image/*" multiple hidden data-testid="outside-photo-input"
              onChange={(e) => { const f = Array.from(e.target.files ?? []); e.currentTarget.value = ''; void addPhotos(outside?.id ?? null, f); }} />
            {canEdit && <Button size="sm" variant="secondary" icon={<ImagePlus size={12} />} disabled={!!uploading} onClick={() => outsideInput.current?.click()} data-testid="outside-add">Add photos</Button>}
          </div>
        </div>
        {outside && outside.note && <div className="text-xs text-muted mt-1 whitespace-pre-wrap">{outside.note}</div>}
        <PhotoStrip clientId={clientId} loc={outside} canEdit={canEdit} onView={setViewing} onChanged={refresh} empty="No outside photos yet. The crew finds the door faster with one." />
      </div>

      {/* ---- inside */}
      {live.length === 0 && q.data && (
        <div className="text-sm text-muted py-2">No inside locations yet. Add the rooms the clinic films in, with the clinic's own names.</div>
      )}
      <div className="space-y-3">
        {live.map((l) => (
          <LocationBlock key={l.id} clientId={clientId} loc={l} canEdit={canEdit} uploading={uploading} onAddPhotos={(files) => void addPhotos(l.id, files)} onView={setViewing} onChanged={refresh} />
        ))}
      </div>

      {/* ---- add */}
      {canEdit && <div className="mt-4 grid grid-cols-1 sm:grid-cols-[1fr_2fr_auto] gap-2 items-end">
        <Input label="New inside location" placeholder="e.g. Laser room 3" value={name} onChange={(e) => setName(e.target.value)} data-testid="location-add-name" />
        <Input label="Note for the crew (optional)" placeholder="Light, power, what must not be moved" value={note} onChange={(e) => setNote(e.target.value)} data-testid="location-add-note" />
        <Button size="sm" icon={<Plus size={12} />} disabled={!name.trim() || create.isPending} loading={create.isPending} onClick={() => create.mutate()} data-testid="location-add-submit">Add location</Button>
      </div>}

      {/* ---- retired */}
      {retired.length > 0 && (
        <div className="mt-4">
          <button type="button" className="text-xs text-muted hover:text-accent-400" onClick={() => setShowRetired((v) => !v)} data-testid="retired-toggle">
            {showRetired ? 'Hide' : 'Show'} retired ({retired.length})
          </button>
          {showRetired && (
            <div className="space-y-3 mt-2 opacity-80">
              {retired.map((l) => (
                <LocationBlock key={l.id} clientId={clientId} loc={l} canEdit={canEdit} uploading={uploading} onAddPhotos={(files) => void addPhotos(l.id, files)} onView={setViewing} onChanged={refresh} />
              ))}
            </div>
          )}
        </div>
      )}

      {viewing && <Lightbox photo={viewing} onClose={() => setViewing(null)} />}
    </Card>
  );
}

function LocationBlock({ clientId, loc, canEdit, uploading, onAddPhotos, onView, onChanged }: {
  clientId: string; loc: ClientLocation; canEdit: boolean; uploading: Uploading;
  onAddPhotos: (files: File[]) => void; onView: (p: LocationPhoto) => void; onChanged: () => void;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [renaming, setRenaming] = useState(false);
  const [noting, setNoting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const patch = useMutation({
    mutationFn: (body: { name?: string; note?: string; active?: boolean }) => patchClientLocation(clientId, loc.id, body),
    onSuccess: (l, body) => {
      toast.success(body.active === false ? `${l.name} retired` : body.active === true ? `${l.name} is back` : body.name ? `Renamed to ${l.name}` : 'Note saved');
      setRenaming(false); setNoting(false); onChanged();
    },
    onError: (e) => toast.error(errMsg(e)),
  });
  const del = useMutation({
    mutationFn: () => deleteClientLocation(clientId, loc.id),
    onSuccess: () => { toast.success(`${loc.name} deleted`); setConfirmDelete(false); onChanged(); },
    onError: (e) => { toast.error(errMsg(e)); setConfirmDelete(false); },
  });
  const busy = !!uploading && uploading.locationId === loc.id;

  return (
    <div className={cn('rounded-lg bg-bg px-3 py-2.5', !loc.active && 'border border-dashed border-slate-800')} data-testid="location" data-kind="inside" data-active={loc.active ? 'true' : 'false'} data-name={loc.name}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm text-muted-strong font-medium" data-testid="location-name">{loc.name}</span>
        {!loc.active && <span className="px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wide bg-slate-700/40 text-slate-300">retired</span>}
        <span className="text-[11px] text-muted">{loc.photos.length} photo{loc.photos.length === 1 ? '' : 's'}</span>
        {canEdit && <div className="ml-auto flex items-center gap-1 flex-wrap">
          {busy && <span className="text-[11px] text-accent-400 mr-1">Uploading {uploading!.done} of {uploading!.total}...</span>}
          <input ref={input} type="file" accept="image/*" multiple hidden data-testid="location-photo-input" data-location={loc.id}
            onChange={(e) => { const f = Array.from(e.target.files ?? []); e.currentTarget.value = ''; onAddPhotos(f); }} />
          <Button size="sm" variant="secondary" icon={<ImagePlus size={12} />} disabled={!!uploading} onClick={() => input.current?.click()} data-testid="location-add-photos">Add photos</Button>
          <button type="button" title="Rename" className="p-2 rounded-lg text-muted hover:text-accent-400 hover:bg-slate-800/60" onClick={() => setRenaming(true)} data-testid="location-rename"><Pencil size={14} /></button>
          <button type="button" title="Note for the crew" className="p-2 rounded-lg text-muted hover:text-accent-400 hover:bg-slate-800/60" onClick={() => setNoting(true)} data-testid="location-note"><StickyNote size={14} /></button>
          {loc.active
            ? <button type="button" title="Retire (old boards keep it)" className="p-2 rounded-lg text-muted hover:text-amber-300 hover:bg-slate-800/60" onClick={() => patch.mutate({ active: false })} data-testid="location-retire"><Archive size={14} /></button>
            : <button type="button" title="Bring back" className="p-2 rounded-lg text-muted hover:text-emerald-300 hover:bg-slate-800/60" onClick={() => patch.mutate({ active: true })} data-testid="location-restore"><ArchiveRestore size={14} /></button>}
          {loc.photos.length === 0 && (
            <button type="button" title="Delete (no photos yet)" className="p-2 rounded-lg text-muted hover:text-red-400 hover:bg-red-500/10" onClick={() => setConfirmDelete(true)} data-testid="location-delete"><Trash2 size={14} /></button>
          )}
        </div>}
      </div>
      {loc.note && <div className="text-xs text-muted mt-1 whitespace-pre-wrap" data-testid="location-note-text">{loc.note}</div>}
      <PhotoStrip clientId={clientId} loc={loc} canEdit={canEdit} onView={onView} onChanged={onChanged} empty="No photos yet." />

      <PromptDialog open={renaming} title="Rename this location" message="Use the clinic's own name for the room, so the crew and the doctor mean the same place." placeholder="e.g. Laser room 3"
        defaultValue={loc.name} confirmLabel="Rename" maxLength={120} onConfirm={(v) => { if (v.trim()) patch.mutate({ name: v.trim() }); }} onCancel={() => setRenaming(false)} />
      <PromptDialog mentions open={noting} title="Note for the crew" message="Light, power, capacity, what must not be moved." placeholder="Big window on the left, one socket by the door, no moving the laser cart"
        defaultValue={loc.note ?? ''} confirmLabel="Save" multiline maxLength={2000} onConfirm={(v) => patch.mutate({ note: v })} onCancel={() => setNoting(false)} />
      <ConfirmDialog open={confirmDelete} title={`Delete ${loc.name}?`} message="It has no photos, so nothing is lost." confirmLabel="Delete" tone="danger" onConfirm={() => del.mutate()} onCancel={() => setConfirmDelete(false)} />
    </div>
  );
}

function PhotoStrip({ clientId, loc, canEdit, onView, onChanged, empty }: { clientId: string; loc: ClientLocation | null; canEdit: boolean; onView: (p: LocationPhoto) => void; onChanged: () => void; empty: string }) {
  const [removing, setRemoving] = useState<LocationPhoto | null>(null);
  const del = useMutation({
    mutationFn: (p: LocationPhoto) => deleteLocationPhoto(clientId, p.location_id, p.id),
    onSuccess: () => { toast.success('Photo removed'); setRemoving(null); onChanged(); },
    onError: (e) => { toast.error(errMsg(e)); setRemoving(null); },
  });
  const photos = loc?.photos ?? [];
  if (!photos.length) return <div className="text-[11px] text-muted mt-1.5">{empty}</div>;
  return (
    <>
      <div className="grid grid-cols-3 sm:grid-cols-5 md:grid-cols-6 gap-2 mt-2">
        {photos.map((p) => (
          <div key={p.id} className="relative group aspect-[4/3] rounded-lg overflow-hidden bg-slate-900 border border-slate-800" data-testid="location-photo">
            <button type="button" className="absolute inset-0 w-full h-full" title={p.caption ?? p.filename} onClick={() => onView(p)} data-compact>
              <img src={p.thumbnail_url ?? p.url ?? undefined} alt={p.caption ?? p.filename} className="w-full h-full object-cover" loading="lazy" />
            </button>
            {canEdit && <button type="button" title="Remove photo" onClick={() => setRemoving(p)} data-testid="photo-delete" data-compact
              className="absolute top-1 right-1 p-1 rounded-md bg-black/60 text-white/80 hover:text-red-300 sm:opacity-0 sm:group-hover:opacity-100 transition"><X size={12} /></button>}
          </div>
        ))}
      </div>
      <ConfirmDialog open={!!removing} title="Remove this photo?" message="It goes from storage too." confirmLabel="Remove" tone="danger"
        onConfirm={() => { if (removing) del.mutate(removing); }} onCancel={() => setRemoving(null)} />
    </>
  );
}

function Lightbox({ photo, onClose }: { photo: LocationPhoto; onClose: () => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-[70] bg-black/85 flex items-center justify-center p-4" onClick={onClose} data-testid="photo-lightbox">
      <img src={photo.url ?? photo.thumbnail_url ?? undefined} alt={photo.caption ?? photo.filename} className="max-w-full max-h-[88vh] rounded-lg shadow-2xl" onClick={(e) => e.stopPropagation()} />
      <button type="button" className="absolute top-3 right-3 p-2 rounded-lg bg-black/60 text-white" onClick={onClose} title="Close"><X size={16} /></button>
      {(photo.caption || photo.filename) && <div className="absolute bottom-3 left-0 right-0 text-center text-xs text-white/80">{photo.caption ?? photo.filename}</div>}
    </div>
  );
}
